import React, { useState } from 'react';
import {
  Map, Network, LineChart, Cpu, ScanText, Brain, Server, Zap, Send, Link2, Bot,
  Database, Shield, DatabaseZap, LayoutTemplate, ShieldCheck, TestTube, CheckCircle,
  Gauge, FileText, ArrowRight,
} from 'lucide-react';

// Arranca el circuito de esta página: entra por la izquierda y baja recto
// todo el borde -- sigue derecho en StackBuilder, cruza al fondo. Violeta
// para leerse sobre el fondo negro.
const ShowcaseCircuit = () => (
  <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
    <div className="absolute left-4 top-0 bottom-0 w-[1.5px] bg-[#8B5CF6] opacity-40" />
  </div>
);

const CATEGORIES = [
  {
    id: 'mapeo',
    title: 'Levantamiento y Mapeo de Procesos',
    description: 'Análisis profundo de tus flujos de trabajo actuales para identificar tareas redundantes y cuellos de botella.',
    icon: Map,
    steps: [
      { title: 'Arquitectura de Soluciones', icon: Network },
      { title: 'Auditoría de Deuda Técnica', icon: LineChart },
    ],
  },
  {
    id: 'ia',
    title: 'Inteligencia Artificial y Agentes Autónomos',
    description: 'Llevamos la IA más allá del chat; la ponemos a ejecutar tareas.',
    icon: Cpu,
    steps: [
      { title: 'Extracción Inteligente (OCR)', icon: ScanText },
      { title: 'Agentes de Decisión Autónoma', icon: Brain },
      { title: 'Modelos Locales On-Premise', icon: Server },
    ],
  },
  {
    id: 'hiper-automatizacion',
    title: 'Hiper-Automatización de Flujos',
    description: 'Conectamos lo desconectado para que la información fluya sola.',
    icon: Zap,
    steps: [
      { title: 'Comunicaciones Masivas', icon: Send },
      { title: 'Orquestación de APIs', icon: Link2 },
      { title: 'Automatización de Tareas (RPA)', icon: Bot },
    ],
  },
  {
    id: 'ecosistemas',
    title: 'Desarrollo de Ecosistemas Core',
    description: 'Sistemas robustos que sirven de base para toda la operación.',
    icon: Database,
    steps: [
      { title: 'Permisos y Seguridad (RBAC)', icon: Shield },
      { title: 'Procesamiento Masivo de Datos', icon: DatabaseZap },
      { title: 'Plataformas a la Medida', icon: LayoutTemplate },
    ],
  },
  {
    id: 'qa',
    title: 'Calidad y Estabilidad (QA Automation)',
    description: 'Software que no falla es software que genera rentabilidad.',
    icon: ShieldCheck,
    steps: [
      { title: 'Pruebas End-to-End', icon: TestTube },
      { title: 'Validación de Integridad', icon: CheckCircle },
      { title: 'Simulación de Carga', icon: Gauge },
    ],
  },
];

// Diagrama de arquitectura: entrada -> pasos del servicio -> salida, con el
// mismo lenguaje visual (badges negros, conectores) que el resto del sitio.
const ArchitectureFlow = ({ steps }) => {
  const nodes = [
    { title: 'Tu operación', icon: FileText, isEndpoint: true },
    ...steps,
    { title: 'Resultado automatizado', icon: CheckCircle, isEndpoint: true },
  ];

  return (
    <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-0">
      {nodes.map((node, i) => (
        <React.Fragment key={node.title}>
          {i > 0 && (
            <ArrowRight
              className="text-[#A78BFA]/60 shrink-0 rotate-90 sm:rotate-0 my-1 sm:my-0 sm:mx-2"
              size={18}
            />
          )}
          <div className="flex flex-col items-center gap-2.5 w-[108px] shrink-0 text-center">
            <div
              className={`rounded-xl flex items-center justify-center shrink-0 ${
                node.isEndpoint ? 'bg-white/5 border border-white/15' : 'bg-white'
              }`}
              style={{ width: 52, height: 52 }}
            >
              <node.icon className={node.isEndpoint ? 'w-5 h-5 text-white/50' : 'w-5 h-5 text-black'} strokeWidth={1.75} />
            </div>
            <span
              className={`text-[12px] leading-tight ${
                node.isEndpoint ? 'text-white/50' : 'text-white font-semibold'
              }`}
            >
              {node.title}
            </span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

const DynamicServicesShowcase = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = CATEGORIES[activeIndex];

  return (
    <section className="relative z-[1] bg-black px-5 sm:px-8 md:px-10 py-16 sm:py-20 overflow-hidden">
      <ShowcaseCircuit />
      <div className="relative z-10 max-w-3xl mb-14 sm:mb-16">
        <h2
          style={{ fontSize: 'clamp(26px, 4.2vw, 44px)', lineHeight: 1.15, fontWeight: 500, letterSpacing: '-0.01em' }}
          className="text-white"
        >
          Nuestros ecosistemas de automatización, en detalle.
        </h2>
      </div>

      <div className="relative z-10 flex flex-col lg:flex-row gap-8 lg:gap-12 max-w-7xl mx-auto">
        <div className="lg:w-1/3 flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
          {CATEGORIES.map((category, index) => {
            const isActive = index === activeIndex;
            return (
              <button
                key={category.id}
                onClick={() => setActiveIndex(index)}
                className={`flex items-center gap-3 text-left px-4 py-3.5 rounded-xl transition-colors duration-200 shrink-0 ${
                  isActive ? 'bg-white text-black' : 'text-white/60 hover:bg-white/10'
                }`}
              >
                <category.icon className="w-5 h-5 shrink-0" strokeWidth={1.75} />
                <span className="text-[14px] sm:text-[15px] font-medium whitespace-nowrap lg:whitespace-normal">
                  {category.title}
                </span>
              </button>
            );
          })}
        </div>

        {/* Todos los paneles quedan en el DOM (los inactivos con hidden) para
            que buscadores y crawlers de IA lean las 5 categorías, no solo la
            que está seleccionada. */}
        {CATEGORIES.map((category) => (
          <div
            key={category.id}
            hidden={category.id !== active.id}
            className="lg:w-2/3 bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 md:p-10"
          >
            <h3 className="sr-only">{category.title}</h3>
            <p className="text-white text-[19px] sm:text-[22px] font-medium leading-snug mb-10" style={{ letterSpacing: '-0.005em' }}>
              {category.description}
            </p>
            <ArchitectureFlow steps={category.steps} />
          </div>
        ))}
      </div>
    </section>
  );
};

export default DynamicServicesShowcase;
